/* =============================================================
   usePartnerProcess.js — Read-only process steps for a partner
   -------------------------------------------------------------
   Fetches the incoming process steps configured by a partner
   university that is connected on Unixplore (has a row in the
   universities table via linked_university_id).

   Used in the student portal to show the partner university's
   incoming process alongside the home university's outgoing
   process — both displayed in parallel.

   Uses a SECURITY DEFINER RPC function to bypass RLS, since
   the student/admin does not own the partner's process data.

   Usage:
     const { steps, loading } = usePartnerProcess(linkedUniversityId);

   Returns:
     steps   — ordered array of { id, label, step_order, description }
     loading — true on the first fetch
   ============================================================= */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
const _cache = new Map();

export function usePartnerProcess(linkedUniversityId) {
  const cached  = linkedUniversityId ? _cache.get(linkedUniversityId) : undefined;

  const [steps,   setSteps]   = useState(cached ?? []);
  const [loading, setLoading] = useState(!!linkedUniversityId && cached === undefined);

  useEffect(() => {
    if (!linkedUniversityId) {
      setSteps([]);
      setLoading(false);
      return;
    }

    // Return cached data immediately, still re-fetch silently
    if (_cache.has(linkedUniversityId)) {
      setSteps(_cache.get(linkedUniversityId));
    } else {
      setLoading(true);
    }

    supabase
      .rpc('get_partner_process_steps', { p_linked_university_id: linkedUniversityId })
      .then(({ data, error }) => {
        if (!error) {
          const result = data ?? [];
          _cache.set(linkedUniversityId, result);
          setSteps(result);
        }
        setLoading(false);
      });
  }, [linkedUniversityId]);

  return { steps, loading };
}
